import { ActionType, getType } from 'typesafe-actions';
import { Presentation, AffectedDevice, Theme } from '@raydiant/api-client-js';
import * as presActions from '../../actions/presentations';
import * as actions from './actions';
import getUnsavedPresentationId from './getUnsavedPresentationId';
import getUnsavedThemeId from './getUnsavedThemeId';
import { SortThemeOptions } from '../../utilities';

export type PresentationPageActions = ActionType<
  typeof actions & typeof presActions
>;

export type PresentationPageState = Readonly<{
  loadingStatus: 'loading' | 'success' | 'error';
  unsavedPresentations: {
    [key: string]: Presentation;
  };
  unsavedThemes: {
    [key: string]: Theme;
  };
  affectedDevices: AffectedDevice[];
  themeSortOptions: SortThemeOptions;
  themeSearchQuery: string;
  // OAuth prefill (CX-4962). Verified credentials keyed by app id → prop name.
  // Deliberately NOT persisted (see index.ts whitelist): refetch each load so no
  // credential lingers in sessionStorage. `prefillStatus[appId] === 'applied'`
  // freezes overlay after the user takes control of the form.
  oauthPrefill: {
    [applicationId: string]: { [propName: string]: string };
  };
  oauthPrefillStatus: {
    [applicationId: string]: 'idle' | 'applied';
  };
  // True once the prefill GET+verify cycle has finished for an app. Gates the
  // form-edit freeze so it cannot engage before prefill lands (race fix).
  oauthPrefillResolved: {
    [applicationId: string]: boolean;
  };
}>;

const initialPresentationPageState: PresentationPageState = {
  loadingStatus: 'loading',
  unsavedPresentations: {},
  unsavedThemes: {},
  affectedDevices: [],
  themeSortOptions: { property: 'name', direction: 'asc' },
  themeSearchQuery: '',
  oauthPrefill: {},
  oauthPrefillStatus: {},
  oauthPrefillResolved: {},
};

export default function presentationPageReducer(
  state = initialPresentationPageState,
  action: PresentationPageActions,
): PresentationPageState {
  switch (action.type) {
    case getType(actions.loadPresentationPageAsync.request):
      return { ...state, loadingStatus: 'loading' };

    case getType(actions.loadPresentationPageAsync.success):
      return {
        ...state,
        loadingStatus: 'success',
        affectedDevices: action.payload.affectedDevices,
      };

    case getType(actions.loadPresentationPageAsync.failure):
      return {
        ...state,
        loadingStatus: 'error',
      };

    case getType(actions.setUnsavedPresentation):
      return {
        ...state,
        unsavedPresentations: {
          ...state.unsavedPresentations,
          [getUnsavedPresentationId(action.payload)]: action.payload,
        },
      };

    case getType(actions.clearUnsavedPresentation):
      const unsavedPresentations = { ...state.unsavedPresentations };
      delete unsavedPresentations[action.payload];

      return {
        ...state,
        unsavedPresentations,
      };

    case getType(actions.clearAllUnsavedPresentations): {
      return {
        ...state,
        unsavedPresentations: {},
      };
    }

    case getType(actions.setUnsavedTheme):
      return {
        ...state,
        unsavedThemes: {
          ...state.unsavedThemes,
          [getUnsavedThemeId(action.payload)]: action.payload,
        },
      };

    case getType(actions.clearUnsavedTheme):
      const unsavedThemes = { ...state.unsavedThemes };
      delete unsavedThemes[action.payload];

      return {
        ...state,
        unsavedThemes,
      };

    case getType(actions.clearAllUnsavedThemes): {
      return {
        ...state,
        unsavedThemes: {},
      };
    }

    case getType(actions.setThemeSortOptions):
      return {
        ...state,
        themeSortOptions: action.payload,
      };

    case getType(actions.setThemeSearchQuery):
      return {
        ...state,
        themeSearchQuery: action.payload,
      };

    case getType(actions.setOAuthPrefill): {
      const { applicationId, propName, value } = action.payload;
      return {
        ...state,
        oauthPrefill: {
          ...state.oauthPrefill,
          [applicationId]: {
            ...state.oauthPrefill[applicationId],
            [propName]: value,
          },
        },
      };
    }

    case getType(actions.markOAuthPrefillApplied):
      return {
        ...state,
        oauthPrefillStatus: {
          ...state.oauthPrefillStatus,
          [action.payload.applicationId]: 'applied',
        },
      };

    case getType(actions.markOAuthPrefillResolved):
      return {
        ...state,
        oauthPrefillResolved: {
          ...state.oauthPrefillResolved,
          [action.payload.applicationId]: true,
        },
      };

    case getType(actions.clearOAuthPrefill): {
      const oauthPrefill = { ...state.oauthPrefill };
      const oauthPrefillStatus = { ...state.oauthPrefillStatus };
      const oauthPrefillResolved = { ...state.oauthPrefillResolved };
      delete oauthPrefill[action.payload.applicationId];
      delete oauthPrefillStatus[action.payload.applicationId];
      delete oauthPrefillResolved[action.payload.applicationId];
      return {
        ...state,
        oauthPrefill,
        oauthPrefillStatus,
        oauthPrefillResolved,
      };
    }

    default:
      return state;
  }
}
